import React, {useCallback, useMemo, useState} from 'react';
import './JobDetailsTab.scss';
import {Box, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import {useGetAIReviewJobDetails} from '../../../../../hooks/ai/review/useReview';
import {IGetAIReviewJobDetailsPayload} from '../../../../../api/types/aiReview.type';
import {toCapitalized} from '../../utils';

const ISSUE_CODING = 'ISSUECODE';
const RELEVANCY = 'RELEVANCY';
export const JobDetailsTab: React.FC<{jobId: string | number}> = ({jobId}) => {
  const {data: getJobDetailData} = useGetAIReviewJobDetails(
    {jobId} as IGetAIReviewJobDetailsPayload,
    true
  );
  const {
    firstName,
    lastName,
    status,
    caseContext,
    issueCodes,
    relevanceCriteria: relevanceCriteria,
    jobType,
    sourceType,
  } = getJobDetailData || {};
  const caseContextParagraphs = useMemo(
    () => caseContext?.split('\n\n') || [],
    [caseContext]
  );

  const getAIReviewJobType = (type: string | undefined) => {
    if (!type) return '';
    switch (type) {
      case ISSUE_CODING:
        return t('ai.review.jobType.issueCoding');
      case RELEVANCY:
        return t('ai.review.jobType.relevancy');
      default:
        return type;
    }
  };

  const topSectionData = [
    {label: t('ai.review.jobDetailsTab.jobId'), value: jobId},
    {
      label: t('ai.review.jobDetailsTab.type'),
      value: getAIReviewJobType(jobType),
    },
    {
      label: t('ai.review.jobDetailsTab.createdBy'),
      value: `${firstName || ''} ${lastName || ''}`,
    },
    {
      label: t('ai.review.jobDetailsTab.status'),
      value: toCapitalized(status ?? ''),
    },
    {label: t('ai.review.jobDetailsTab.selectedSource'), value: sourceType},
  ];
  const useScrollBorder = () => {
    const [isScrolled, setIsScrolled] = useState(false);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = e.currentTarget.scrollTop;

      setIsScrolled(scrollTop > 0);
    }, []);

    return {isScrolled, handleScroll};
  };
  const caseContextScroll = useScrollBorder();
  const contentBoxScroll = useScrollBorder();
  return (
    <Box className="job-details-tab-container">
      <Grid
        container
        spacing={2}
        className="top-div"
        data-testid="job-details-top-container"
      >
        {topSectionData.map(({label, value}, index) => (
          <Grid
            size={{xs: 12, md: 2, sm: 4}}
            className="top-section"
            key={label}
            tabIndex={0}
            data-testid="job-details-top-box"
          >
            <Typography
              className="typo-text label3_size top-section-title"
              data-testid={`job-details-top-label-${index}`}
            >
              {label}
            </Typography>
            <Typography
              className="typo-text label3_size top-section-text"
              data-testid={`job-details-top-value-${index}`}
            >
              {value}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <div className="bottom-div" data-testid={`job-details-top-wrapper`}>
        <div className="content-div" data-testid="job-details-tab-wrappe-2">
          <Typography
            tabIndex={0}
            className={`typo-text subhead4_size content-heading ${caseContextScroll.isScrolled ? 'heading-border' : ''}`}
            data-testid="job-details-tab-case-context"
          >
            {t('ai.review.jobDetailsTab.caseContext')}
          </Typography>
          <div
            className=" case-context-para-div"
            onScroll={caseContextScroll.handleScroll}
            tabIndex={0}
            data-testid="job-details-tab-case-context-paragraphs"
          >
            {caseContextParagraphs.map((para, i) => {
              return (
                <Typography
                  key={i}
                  className="typo-text body3_size"
                  data-testid={`job-details-tab-para-item-${i}`}
                >
                  {para}
                </Typography>
              );
            })}
          </div>
        </div>
        <div
          className="content-div"
          data-testid="job-details-tab-div-btn-wrapper"
        >
          <Typography
            className={`typo-text subhead4_size content-heading ${contentBoxScroll.isScrolled ? 'heading-border' : ''}`}
            tabIndex={0}
            data-testid={`job-details-tab-typography-${jobType === ISSUE_CODING ? 'issue-codes' : 'relevance'}`}
          >
            {jobType === ISSUE_CODING
              ? t('ai.review.jobDetailsTab.issueCodes')
              : t('ai.review.jobDetailsTab.relevanceCriteria')}
          </Typography>
          <div
            className="content-box"
            onScroll={contentBoxScroll.handleScroll}
            data-testid="job-details-tab-div-link"
            tabIndex={0}
          >
            {jobType === ISSUE_CODING ? (
              issueCodes?.map(({question, code}, index) => {
                return (
                  <div
                    className="issue-code-div"
                    data-testid={`issue-code-wrapper-${index}`}
                    key={`issue-code-wrapper-${index}`}
                    tabIndex={0}
                  >
                    <Typography
                      className="typo-text subhead5_size"
                      data-testid={`issue-code-title-${index}`}
                    >
                      {code}
                    </Typography>
                    <Typography
                      className="typo-text body3_size issue-code-text"
                      data-testid={`issue-code-text-${index}`}
                    >
                      {question}
                    </Typography>
                  </div>
                );
              })
            ) : (
              <Typography
                className="typo-text body3_size "
                data-testid={`relevance-criteria-text`}
              >
                {relevanceCriteria}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Box>
  );
};
